// Phase SA-5b/2 — Slip image upload dropzone.
//
// Drag-and-drop OR file picker → validates (mime + size) →
// uploads to the `slip-images` Supabase Storage bucket at the
// user-scoped path `{userId}/{YYYYMMDD}_{rand}.{ext}` (so the
// SA-5b/1 RLS policies allow the write) → returns
// { path, mime, sizeBytes } to the parent form.
//
// State machine driven by `data-state` so the SA-3 CSS handles
// the visuals:
//   idle      → headline + Choose file CTA
//   dragging  → saffron border + amber-glow background
//   uploading → spinner + filename
//   uploaded  → thumbnail (image) or PDF badge + Remove
//   error     → red border + inline message
//
// N01: never expose raw vendor errors — the catch branch maps to
// a short user-facing hint string.

import { useEffect, useId, useRef, useState } from "react";

import { getBrowserClient } from "../../supabase/browser.ts";
import {
  ALLOWED_MIMES,
  generatePath,
  humanSize,
  isImageMime,
  validateFile,
} from "../../slips/slipUploadHelpers.ts";

export interface SlipUploadValue {
  /** Storage path inside the `slip-images` bucket. */
  path: string;
  mime: string;
  sizeBytes: number;
  /** Original filename (for display). Not persisted. */
  fileName: string;
}

export interface SlipUploadDropzoneProps {
  /** Authenticated user id — used as the first path segment so
   * storage RLS (`split_part(name, '/', 1) = auth.uid()`) passes. */
  userId: string;
  /** Controlled current value (null when nothing uploaded). */
  value: SlipUploadValue | null;
  /** Parent receives the successful upload. */
  onUploadComplete: (value: SlipUploadValue) => void;
  /** Parent clears its state. */
  onClear: () => void;
  /** Disable inputs while the parent submits. */
  disabled?: boolean;
}

type DropzoneState =
  | "idle"
  | "dragging"
  | "uploading"
  | "uploaded"
  | "error";

const ACCEPT_ATTR = ALLOWED_MIMES.join(",");

export function SlipUploadDropzone({
  userId,
  value,
  onUploadComplete,
  onClear,
  disabled,
}: SlipUploadDropzoneProps): JSX.Element {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [state, setState] = useState<DropzoneState>(
    value ? "uploaded" : "idle",
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [progressName, setProgressName] = useState<string | null>(null);
  // Object URL for the preview thumbnail (image uploads only).
  // Lives in component state so we can revoke on unmount / clear.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Keep local state in sync when parent clears externally.
  useEffect(() => {
    if (!value) {
      setState((prev) => (prev === "uploading" ? prev : "idle"));
    } else {
      setState("uploaded");
    }
  }, [value]);

  // Revoke object URL when component unmounts or url changes.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  async function handleFile(file: File): Promise<void> {
    setErrorMsg(null);
    const reject = validateFile(file);
    if (reject) {
      setState("error");
      setErrorMsg(reject);
      return;
    }

    setProgressName(file.name);
    setState("uploading");

    try {
      const path = generatePath(userId, file.type);
      const supabase = getBrowserClient();
      const { error } = await supabase.storage
        .from("slip-images")
        .upload(path, file, {
          contentType: file.type,
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        // N01 — never surface vendor error verbatim.
        setState("error");
        setErrorMsg("Upload failed. Please try again.");
        return;
      }

      // Generate preview URL for images (PDFs render as badge).
      if (isImageMime(file.type)) {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(file));
      } else {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }

      onUploadComplete({
        path,
        mime: file.type,
        sizeBytes: file.size,
        fileName: file.name,
      });
      setState("uploaded");
    } catch {
      setState("error");
      setErrorMsg("Upload failed. Please try again.");
    }
  }

  function onDragOver(e: React.DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    if (disabled || state === "uploading") return;
    if (state !== "uploaded") setState("dragging");
  }

  function onDragLeave(e: React.DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    if (state === "dragging") setState(value ? "uploaded" : "idle");
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    if (disabled || state === "uploading") return;
    const file = e.dataTransfer.files?.[0];
    if (file) {
      void handleFile(file);
    } else {
      setState(value ? "uploaded" : "idle");
    }
  }

  function onPick(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    // Reset so picking the same file twice still triggers onChange.
    e.target.value = "";
    if (file) void handleFile(file);
  }

  function onRemove(): void {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setProgressName(null);
    setErrorMsg(null);
    setState("idle");
    onClear();
  }

  // Open the OS file picker programmatically. The whole drop area is the
  // click target (not just the small "Choose file" label) — and this never
  // depends on the <label htmlFor>/useId association, which silently fails to
  // trigger the hidden input on some surfaces. Guarded so clicks during
  // uploading / on the uploaded preview (Remove) don't re-open the picker.
  const pickable = state === "idle" || state === "dragging" || state === "error";
  function openPicker(): void {
    if (disabled || !pickable) return;
    inputRef.current?.click();
  }

  return (
    <div
      className="sa-upload-dropzone"
      data-state={state}
      data-testid="sa-upload-dropzone"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={openPicker}
      style={{ cursor: pickable ? "pointer" : "default" }}
    >
      {(state === "idle" || state === "dragging" || state === "error") && (
        <>
          <div className="sa-upload-headline">
            {state === "dragging"
              ? "Drop your slip image to upload"
              : "Snap or drop your slip image"}
          </div>
          <div className="sa-upload-sub">
            JPG · PNG · HEIC · WEBP · PDF — up to 12 MB
          </div>
          {state === "error" && errorMsg && (
            <div
              className="sa-upload-error-text"
              data-testid="sa-upload-error"
              role="alert"
            >
              {errorMsg}
            </div>
          )}
          {/* Visual CTA only — the whole dropzone is the click target via
              the container onClick (openPicker). No htmlFor: that would
              double-trigger the picker AND its useId association is the very
              thing that was failing to open the dialog. */}
          <span
            className="sa-upload-picker-btn"
            data-testid="sa-upload-picker-btn"
            aria-hidden="true"
          >
            Choose file
          </span>
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept={ACCEPT_ATTR}
            onChange={onPick}
            disabled={disabled}
            style={{ display: "none" }}
            data-testid="sa-upload-input"
          />
        </>
      )}

      {state === "uploading" && (
        <div
          className="sa-upload-uploading"
          data-testid="sa-upload-uploading"
          role="status"
        >
          <span className="sa-upload-spinner" aria-hidden />
          <span>Uploading {progressName ?? "your slip"}…</span>
        </div>
      )}

      {state === "uploaded" && value && (
        <div
          className="sa-upload-preview"
          data-testid="sa-upload-preview"
        >
          {isImageMime(value.mime) && previewUrl ? (
            <img
              className="sa-upload-thumb"
              src={previewUrl}
              alt="Slip preview"
            />
          ) : (
            <div
              className="sa-upload-pdf-badge"
              data-testid="sa-upload-pdf-badge"
            >
              <span className="sa-upload-pdf-badge-glyph">PDF</span>
              <span>SLIP FILE</span>
            </div>
          )}
          <div className="sa-upload-meta">
            <span className="sa-upload-meta-name" title={value.fileName}>
              {value.fileName}
            </span>
            <span className="sa-upload-meta-sub">
              {humanSize(value.sizeBytes)}
            </span>
            <span className="sa-upload-meta-ready">Ready for autopsy</span>
          </div>
          <button
            type="button"
            className="sa-upload-remove-btn"
            onClick={onRemove}
            disabled={disabled}
            data-testid="sa-upload-remove-btn"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}
